import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthContext } from '../../providers';

import useOnSignInCallback from './useOnSignCallback';
import useOnCompleteNewPasswordCallback from './useOnCompleteNewPasswordCallback';
import useOnSignInFederate from './useOnSignInFederate';

import * as actions from '../../store/actions';
import { getIsLoading } from '../../store';

export const useSignIn = (redirectHome = false) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const loading = useSelector(getIsLoading);
  const {
    signInPathname,
    signInRedirectPathname,
    isExternalAuth,
  } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(actions.setLoading(false));
  }, [dispatch]);

  const handleSignIn = useCallback(async (user, isFederated) => {
    const routeFromPathname = location.state?.from?.pathname;
    const from = (
      routeFromPathname !== signInPathname
        ? routeFromPathname || signInRedirectPathname
        : signInRedirectPathname
    ) || '/';

    dispatch(
      actions.handleSignIn(user, isFederated, {
        singlePathname: from,
        navigate,
        isExternalAuth,
        redirectHome,
      })
    );
  }, [
    redirectHome,
    dispatch,
    isExternalAuth,
    location.state?.from?.pathname,
    navigate,
    signInPathname,
    signInRedirectPathname
  ]);

  const {
    onSignIn,
    response,
    isNewPasswordRequired,
    setIsNewPasswordRequired,
  } = useOnSignInCallback(handleSignIn);

  const handleSignInChangePassword = useCallback(async () => {
    dispatch(actions.removeUser());
    dispatch(actions.setLoading(false));
    setIsNewPasswordRequired(false);
  }, [
    dispatch,
    setIsNewPasswordRequired
  ]);

  const onCompleteNewPassword = useOnCompleteNewPasswordCallback(
    handleSignInChangePassword,
    response,
    setIsNewPasswordRequired
  );
  const onSignInFederate = useOnSignInFederate();

  const onSubmit = async (values, helpers) => {
    if (isNewPasswordRequired) {
      return onCompleteNewPassword(values, helpers);
    }
    return onSignIn(values, helpers);
  };

  return {
    loading,
    isNewPasswordRequired,
    onSubmit,
    onSignInFederate,
    handleSignIn,
  };
};
